import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mail, Linkedin, MapPin, ExternalLink, Calendar, Award, Book, Code2, Cpu, Server, Database, Globe, ArrowRight, Activity, TrendingUp, Zap, Terminal, Binary, GraduationCap, FileText, Github } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import '../hpc-fintech.css';

const Landing = () => {
  const [scrollY, setScrollY] = useState(0);
  const [nodeStates, setNodeStates] = useState([]);
  const [txnFeed, setTxnFeed] = useState([]);
  const [logLines, setLogLines] = useState([]);
  const [showTerminal, setShowTerminal] = useState(false);
  const [termInput, setTermInput] = useState('');
  const [termHistory, setTermHistory] = useState([{ type: 'system', text: 'Welcome to johnmaia@dev. Type "help" for commands.' }]);
  const [duckFly, setDuckFly] = useState(false);
  const [photoGlitch, setPhotoGlitch] = useState(false);
  const [overclocked, setOverclocked] = useState(false);
  const [crtMode, setCrtMode] = useState(false);
  const [hackMode, setHackMode] = useState(false);

  // Initialize and animate HPC node grid
  useEffect(() => {
    const TOTAL_NODES = 40;
    const states = ['idle', 'computing', 'active', 'transferring', 'complete'];
    const initNodes = Array.from({ length: TOTAL_NODES }, (_, i) => ({
      id: i,
      state: states[Math.floor(Math.random() * states.length)],
      load: Math.floor(Math.random() * 100),
    }));
    setNodeStates(initNodes);

    const interval = setInterval(() => {
      setNodeStates(prev => prev.map(node => {
        if (Math.random() < 0.15) {
          return {
            ...node,
            state: states[Math.floor(Math.random() * states.length)],
            load: Math.floor(Math.random() * 100),
          };
        }
        return node;
      }));
    }, 800);
    return () => clearInterval(interval);
  }, []);

  // Simulated fintech transaction feed
  useEffect(() => {
    const types = ['SETTLED', 'CLEARING', 'PROCESSED', 'VERIFIED'];
    const pairs = ['USD/EUR', 'BTC/USD', 'ETH/GBP', 'JPY/USD', 'GBP/CHF'];
    const makeTxn = (id) => ({
      id,
      hash: `0x${Math.random().toString(16).slice(2, 8)}`,
      amount: (Math.random() * 50000 + 500).toFixed(0),
      type: types[Math.floor(Math.random() * types.length)],
      pair: pairs[Math.floor(Math.random() * pairs.length)],
      ms: Math.floor(Math.random() * 80 + 12),
    });
    setTxnFeed(Array.from({ length: 3 }, (_, i) => makeTxn(i)));

    let counter = 3;
    const txnInterval = setInterval(() => {
      setTxnFeed(prev => [makeTxn(counter++), ...prev.slice(0, 2)]);
    }, 2200);
    return () => clearInterval(txnInterval);
  }, []);

  // Live log background
  useEffect(() => {
    const pool = [
      { level: 'INFO', color: 'text-cyan-500', msg: 'cluster.node[017] — heartbeat OK latency=2ms' },
      { level: 'DEBUG', color: 'text-slate-500', msg: 'txn.engine — processing batch #48291 size=1024' },
      { level: 'INFO', color: 'text-cyan-500', msg: 'scheduler.hpc — task allocated gpu_node_03 priority=HIGH' },
      { level: 'WARN', color: 'text-yellow-500', msg: 'memory.pool — allocation near threshold 87% on node[008]' },
      { level: 'INFO', color: 'text-cyan-500', msg: 'api.gateway — POST /v2/transactions 201 12ms' },
      { level: 'DEBUG', color: 'text-slate-500', msg: 'compute.grid — rebalancing workload across 40 nodes' },
      { level: 'INFO', color: 'text-cyan-500', msg: 'txn.settle — confirmed TXN#0xfa82d1 USD/EUR $18,420' },
      { level: 'ERROR', color: 'text-red-500', msg: 'network.io — timeout on node[022] retrying in 500ms' },
      { level: 'INFO', color: 'text-cyan-500', msg: 'cluster.node[003] — heartbeat OK latency=1ms' },
      { level: 'DEBUG', color: 'text-slate-500', msg: 'parallel.exec — thread_pool utilization 94.2%' },
      { level: 'INFO', color: 'text-cyan-500', msg: 'scheduler.hpc — completed job #7741 runtime=847ms' },
      { level: 'WARN', color: 'text-yellow-500', msg: 'network.io — retrying connection to node[022] attempt=2' },
      { level: 'INFO', color: 'text-cyan-500', msg: 'txn.engine — batch #48291 committed 1024/1024 OK' },
      { level: 'DEBUG', color: 'text-slate-500', msg: 'cache.layer — hit_ratio=0.973 evictions=12' },
      { level: 'INFO', color: 'text-cyan-500', msg: 'api.gateway — GET /v2/portfolio/summary 200 3ms' },
      { level: 'INFO', color: 'text-cyan-500', msg: 'cluster.node[022] — reconnected latency=4ms' },
      { level: 'DEBUG', color: 'text-slate-500', msg: 'scheduler.hpc — queue depth=12 avg_wait=23ms' },
      { level: 'INFO', color: 'text-cyan-500', msg: 'txn.settle — confirmed TXN#0x3bc91a BTC/USD $42,180' },
      { level: 'INFO', color: 'text-cyan-500', msg: 'compute.grid — node[031] scaling up 4 workers' },
      { level: 'DEBUG', color: 'text-slate-500', msg: 'memory.pool — gc cycle completed freed=2.1GB' },
      { level: 'INFO', color: 'text-cyan-500', msg: 'parallel.exec — mapreduce job #228 complete 12.4s' },
      { level: 'WARN', color: 'text-yellow-500', msg: 'txn.engine — high latency on batch #48292 p99=120ms' },
      { level: 'INFO', color: 'text-cyan-500', msg: 'api.gateway — POST /v2/orders 201 8ms' },
      { level: 'DEBUG', color: 'text-slate-500', msg: 'cluster.health — all 40 nodes reporting OK' },
      { level: 'INFO', color: 'text-cyan-500', msg: 'scheduler.hpc — preempting low-priority task on gpu_node_07' },
      { level: 'ERROR', color: 'text-red-500', msg: 'txn.validate — rejected TXN#0xee12f3 insufficient margin' },
      { level: 'INFO', color: 'text-cyan-500', msg: 'txn.settle — clearing batch EUR/GBP 847 transactions' },
      { level: 'DEBUG', color: 'text-slate-500', msg: 'compute.grid — load avg 85.5% across cluster' },
      { level: 'INFO', color: 'text-cyan-500', msg: 'api.gateway — WebSocket clients connected: 2,847' },
      { level: 'WARN', color: 'text-yellow-500', msg: 'scheduler.hpc — gpu_node_12 temp 78°C approaching limit' },
    ];
    const MAX_LINES = 55;
    let idx = 0;
    const seed = Array.from({ length: 35 }, (_, i) => {
      const entry = pool[i % pool.length];
      return { ...entry, id: i, ts: `09:${String(12 + (i % 5)).padStart(2,'0')}:${String((i * 3) % 60).padStart(2,'0')}.${String(Math.floor(Math.random() * 999)).padStart(3,'0')}` };
    });
    setLogLines(seed);
    idx = 35;
    const interval = setInterval(() => {
      const entry = pool[idx % pool.length];
      const now = new Date();
      const ts = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}.${String(now.getMilliseconds()).padStart(3,'0')}`;
      setLogLines(prev => [...prev.slice(-(MAX_LINES - 1)), { ...entry, id: idx, ts }]);
      idx++;
    }, 300);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Konami Code: ↑↑↓↓←→←→BA
  useEffect(() => {
    const konamiCode = ['ArrowUp','ArrowUp','ArrowDown','ArrowDown','ArrowLeft','ArrowRight','ArrowLeft','ArrowRight','b','a'];
    let pos = 0;
    const handler = (e) => {
      if (e.key === konamiCode[pos]) {
        pos++;
        if (pos === konamiCode.length) {
          setDuckFly(true);
          setTimeout(() => setDuckFly(false), 4000);
          pos = 0;
        }
      } else {
        pos = 0;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Console ASCII art
  useEffect(() => {
    console.log('%c' + [
      '     _       _               __  __       _       ',
      '    | | ___ | |__  _ __     |  \\/  | __ _(_) __ _ ',
      "    | |/ _ \\| '_ \\| '_ \\   | |\\/| |/ _` | |/ _` |",
      '    | | (_) | | | | | | |  | |  | | (_| | | (_| |',
      '    |_|\\___/|_| |_|_| |_|  |_|  |_|\\__,_|_|\\__,_|',
      '',
      '    HPC & Fintech Engineer',
      '    hi@johnmaia.dev',
      '',
      '    Found the console? Type "hack" in the secret terminal.',
      '    Click the logo to open it.',
    ].join('\n'), 'color: #10b981; font-family: monospace; font-size: 12px;');
  }, []);

  // CRT mode: press T 3 times
  useEffect(() => {
    let tCount = 0;
    let tTimer = null;
    const handler = (e) => {
      if (e.key === 't' || e.key === 'T') {
        if (document.activeElement?.tagName === 'INPUT') return;
        tCount++;
        clearTimeout(tTimer);
        tTimer = setTimeout(() => { tCount = 0; }, 800);
        if (tCount === 3) {
          setCrtMode(prev => !prev);
          tCount = 0;
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => { window.removeEventListener('keydown', handler); clearTimeout(tTimer); };
  }, []);

  // Terminal command handler
  const handleTermCommand = (cmd) => {
    const c = cmd.trim().toLowerCase();
    const responses = {
      help: 'Commands: help, whoami, sudo hire, skills, duck, hack, matrix, clear, exit',
      whoami: 'John Maia — Senior Software Engineer | HPC & Fintech | Braga, Portugal',
      'sudo hire': '✓ Request sent. Expect a response at hi@johnmaia.dev within 24h.',
      skills: 'high-perf-computing [96%] | parallel-computing [91%] | fintech-systems [85%] | distributed-systems [82%]',
      duck: '🦆 quack quack! The rubber duck debugger approves.',
      hack: '__HACK__',
      matrix: '__HACK__',
      exit: '__EXIT__',
      clear: '__CLEAR__',
    };
    const res = responses[c] || `command not found: ${cmd}. Type "help" for available commands.`;
    if (res === '__EXIT__') { setShowTerminal(false); return; }
    if (res === '__CLEAR__') { setTermHistory([{ type: 'system', text: 'Terminal cleared.' }]); return; }
    if (res === '__HACK__') {
      setShowTerminal(false);
      setHackMode(true);
      setTimeout(() => setHackMode(false), 5000);
      return;
    }
    setTermHistory(prev => [...prev, { type: 'cmd', text: cmd }, { type: 'output', text: res }]);
  };

  // Overclock handler
  const handleOverclock = () => {
    setOverclocked(true);
    setNodeStates(prev => prev.map(n => ({ ...n, state: 'computing', load: 100 })));
    setTimeout(() => setOverclocked(false), 3000);
  };

  // CERN particle collision canvas
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  const initParticles = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const resize = () => { canvas.width = canvas.offsetWidth * 2; canvas.height = canvas.offsetHeight * 2; };
    resize();
    window.addEventListener('resize', resize);

    const W = () => canvas.width;
    const H = () => canvas.height;

    let phase = 'approach'; // approach | collision | scatter | fade
    let timer = 0;
    const sparks = [];
    let beamL = { x: 0, y: 0 }; // left beam particle
    let beamR = { x: 0, y: 0 }; // right beam particle
    let flashAlpha = 0;

    const trailColors = [
      [239, 68, 68],   // red
      [249, 115, 22],  // orange
      [234, 179, 8],   // yellow
      [16, 185, 129],  // emerald
      [139, 92, 246],  // purple
      [255, 255, 255], // white
    ];

    const reset = () => {
      phase = 'approach';
      timer = 0;
      sparks.length = 0;
      flashAlpha = 0;
      beamL = { x: -20, y: H() / 2 };
      beamR = { x: W() + 20, y: H() / 2 };
    };
    reset();

    const spawnCollision = () => {
      const cx = W() / 2;
      const cy = H() / 2;
      for (let i = 0; i < 35; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 1 + Math.random() * 4;
        const color = trailColors[Math.floor(Math.random() * trailColors.length)];
        // Curve direction simulates magnetic field bending
        const curve = (Math.random() - 0.5) * 0.03;
        sparks.push({
          x: cx, y: cy,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          curve,
          life: 1,
          decay: 0.003 + Math.random() * 0.005,
          color,
          size: 1 + Math.random() * 2,
          trail: [],
        });
      }
    };

    const animate = () => {
      ctx.fillStyle = 'rgba(2, 6, 23, 0.15)';
      ctx.fillRect(0, 0, W(), H());

      const cx = W() / 2;
      const cy = H() / 2;

      if (phase === 'approach') {
        timer++;
        const speed = 2 + timer * 0.08;
        beamL.x += speed;
        beamR.x -= speed;

        // Draw beam trails
        const drawBeam = (bx, by, dir) => {
          for (let i = 0; i < 8; i++) {
            const tx = bx - dir * i * 6;
            const alpha = 1 - i * 0.12;
            ctx.beginPath();
            ctx.arc(tx, by + (Math.random() - 0.5) * 2, 3 - i * 0.3, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(200, 220, 255, ${alpha * 0.8})`;
            ctx.fill();
          }
          // Glow
          const g = ctx.createRadialGradient(bx, by, 0, bx, by, 15);
          g.addColorStop(0, 'rgba(200, 220, 255, 0.6)');
          g.addColorStop(1, 'rgba(100, 150, 255, 0)');
          ctx.beginPath();
          ctx.arc(bx, by, 15, 0, Math.PI * 2);
          ctx.fillStyle = g;
          ctx.fill();
        };
        drawBeam(beamL.x, beamL.y, 1);
        drawBeam(beamR.x, beamR.y, -1);

        // Faint beam line
        ctx.beginPath();
        ctx.moveTo(0, cy);
        ctx.lineTo(W(), cy);
        ctx.strokeStyle = 'rgba(100, 150, 255, 0.05)';
        ctx.lineWidth = 1;
        ctx.stroke();

        if (beamL.x >= cx - 5) {
          phase = 'collision';
          timer = 0;
          flashAlpha = 1;
          spawnCollision();
        }
      }

      if (phase === 'collision') {
        timer++;
        flashAlpha = Math.max(0, 1 - timer * 0.025);

        if (flashAlpha > 0) {
          const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, 60 + timer * 3);
          g.addColorStop(0, `rgba(255, 255, 255, ${flashAlpha})`);
          g.addColorStop(0.3, `rgba(255, 200, 100, ${flashAlpha * 0.5})`);
          g.addColorStop(1, `rgba(255, 100, 50, 0)`);
          ctx.beginPath();
          ctx.arc(cx, cy, 60 + timer * 3, 0, Math.PI * 2);
          ctx.fillStyle = g;
          ctx.fill();
        }

        if (timer > 40) {
          phase = 'scatter';
          timer = 0;
        }
      }

      if (phase === 'scatter' || phase === 'collision') {
        // Update sparks with curved trajectories
        for (let i = sparks.length - 1; i >= 0; i--) {
          const s = sparks[i];
          // Apply curve (magnetic field bending)
          const cos = Math.cos(s.curve);
          const sin = Math.sin(s.curve);
          const nvx = s.vx * cos - s.vy * sin;
          const nvy = s.vx * sin + s.vy * cos;
          s.vx = nvx;
          s.vy = nvy;
          s.x += s.vx;
          s.y += s.vy;
          s.life -= s.decay;
          s.trail.push({ x: s.x, y: s.y });
          if (s.trail.length > 30) s.trail.shift();

          if (s.life <= 0) { sparks.splice(i, 1); continue; }

          // Draw trail
          for (let t = 0; t < s.trail.length - 1; t++) {
            const alpha = (t / s.trail.length) * s.life * 0.5;
            ctx.beginPath();
            ctx.moveTo(s.trail[t].x, s.trail[t].y);
            ctx.lineTo(s.trail[t + 1].x, s.trail[t + 1].y);
            ctx.strokeStyle = `rgba(${s.color.join(',')}, ${alpha})`;
            ctx.lineWidth = s.size * (t / s.trail.length) * s.life;
            ctx.stroke();
          }

          // Draw particle head
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.size * s.life, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${s.life * 0.9})`;
          ctx.fill();
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.size * s.life * 2.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${s.color.join(',')}, ${s.life * 0.2})`;
          ctx.fill();
        }
      }

      if (phase === 'scatter') {
        timer++;
        if (sparks.length === 0 || timer > 300) {
          phase = 'fade';
          timer = 0;
        }
      }

      if (phase === 'fade') {
        timer++;
        if (timer > 60) reset();
      }

      animRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(animRef.current); };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const skills = [
    { name: 'high-perf-computing', pid: '001', cpu: 96, status: 'RUNNING' },
    { name: 'parallel-computing', pid: '002', cpu: 91, status: 'RUNNING' },
    { name: 'backend-development', pid: '003', cpu: 88, status: 'RUNNING' },
    { name: 'fintech-systems', pid: '004', cpu: 85, status: 'RUNNING' },
    { name: 'distributed-systems', pid: '005', cpu: 82, status: 'RUNNING' },
    { name: 'c-programming', pid: '006', cpu: 78, status: 'ACTIVE' },
    { name: 'node-js', pid: '007', cpu: 74, status: 'ACTIVE' },
    { name: 'software-engineering', pid: '008', cpu: 90, status: 'RUNNING' },
  ];

  const experiences = [
    { company: 'Undisclosed Fintech Company', role: 'Senior Software Engineer', period: 'Mar 2025 - Present', duration: '1yr', location: 'Remote', current: true, desc: '' },
    { company: 'Undisclosed Fintech Company', role: 'Software Engineer', period: 'Nov 2021 - Mar 2025', duration: '4y 4m', location: 'Remote', current: false, desc: '' },
    { company: 'Seegno', role: 'Software Engineer, Backend Developer', period: 'May 2017 - Nov 2021', duration: '4y 7m', location: 'Braga, PT', current: false, desc: '' },
    { company: 'WeDo Technologies', role: 'Consulting', period: 'Mar 2017 - May 2017', duration: '3m', location: 'Braga, PT', current: false, desc: 'Built parallel C++ tools for fraud detection across telecom clients (Vodafone, Verizon, Orange).' },
    { company: 'Universidade do Minho', role: 'Master Thesis - HPC', period: 'Sep 2015 - Dec 2016', duration: '1y 4m', location: 'Braga, PT', current: false, desc: 'Dynamic adaptive scheduler for irregular workloads on heterogeneous servers (CPU, GPU, MIC).' },
    { company: 'UT Austin', role: 'Advanced Computing Internship', period: 'Jul 2015 - Aug 2015', duration: '2m', location: 'Austin, TX', current: false, desc: 'Developed a runtime scheduler that learns task performance across heterogeneous hardware.' },
    { company: 'Universidade do Minho / CERN', role: 'ATLAS Experiment - HPC Research', period: 'Mar 2015 - Jun 2015', duration: '4m', location: 'Braga, PT', current: false, desc: 'Achieved 32x speedup in pseudorandom number generation for event reconstruction at CERN\'s ATLAS experiment.' },
  ];

  return (
    <div className={`min-h-screen bg-slate-950 text-white overflow-hidden ${crtMode ? 'crt-mode' : ''}`}>
      {/* HPC Grid Background */}
      <div className="fixed inset-0 hpc-grid pointer-events-none opacity-20" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-xl font-bold font-mono flex items-center gap-2 cursor-pointer" onClick={() => setShowTerminal(!showTerminal)} title="Click to open terminal">
              <span className="text-emerald-400">&gt;</span>
              <span className="text-white">johnmaia</span>
              <span className="text-emerald-400">@</span>
              <span className="text-blue-400">dev</span>
              <span className="text-emerald-400 animate-pulse">_</span>
            </div>
            <div className="hidden md:flex items-center gap-6 font-mono text-sm">
              {['about', 'experience', 'education', 'skills', 'contact'].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className="text-slate-400 hover:text-emerald-400 transition-colors"
                >
                  <span className="text-emerald-400">./</span>{section}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <a href="mailto:hi@johnmaia.dev">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-emerald-400 hover:bg-slate-800">
                  <Mail className="w-4 h-4" />
                </Button>
              </a>
              <a href="https://www.linkedin.com/in/john-maia/" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-blue-400 hover:bg-slate-800">
                  <Linkedin className="w-4 h-4" />
                </Button>
              </a>
              <a href="https://github.com/johnmaia" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-200 hover:bg-slate-800">
                  <Github className="w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-28 pb-16">
        <div className="absolute inset-0 tech-grid-overlay opacity-20" />
        
        {/* Live Log Background */}
        <div className="absolute inset-0 overflow-hidden opacity-[0.07] pointer-events-none select-none font-mono text-[11px] leading-[18px]">
          <div className="flex flex-col justify-end h-full px-4 pb-2">
            {logLines.map((line) => (
              <div key={line.id} className="whitespace-nowrap log-line-enter">
                <span className="text-slate-600">[{line.ts}]</span>{' '}
                <span className={`font-bold ${line.color}`}>{String(line.level).padEnd(5)}</span>{' '}
                <span className="text-slate-400">{line.msg}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="reveal space-y-8">
              {/* Mobile profile photo */}
              <div className="flex lg:hidden justify-center">
                <div className="relative p-1 rounded-full bg-gradient-to-br from-emerald-500 via-blue-500 to-purple-500 shadow-2xl shadow-emerald-500/30 cursor-pointer" onClick={() => { setPhotoGlitch(true); setTimeout(() => setPhotoGlitch(false), 600); }}>
                  <div className={`w-28 h-28 rounded-full overflow-hidden bg-slate-900 border-4 border-slate-900 ${photoGlitch ? 'photo-glitch' : ''}`}>
                    <img src="https://customer-assets.emergentagent.com/job_pro-landing-29/artifacts/bc5dv18k_FB_IMG_1554564668270.jpg" alt="John Maia" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 font-mono text-sm text-slate-400">
                <Activity className="w-4 h-4 text-emerald-400 animate-pulse" />
                <span className="text-emerald-400">ONLINE</span>
                <span className="text-slate-600">|</span>
                <MapPin className="w-4 h-4" />
                <span>Braga, Portugal</span>
              </div>

              <div>
                <div className="text-emerald-400 font-mono text-sm mb-3">$ cat ~/profile.txt</div>
                <h1 className="text-7xl md:text-8xl font-black font-mono mb-4">
                  <span className="block text-white">John</span>
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-blue-400 to-purple-400">
                    Maia
                  </span>
                </h1>
                <div className="flex gap-2 mb-6">
                  <div className="h-1 w-16 bg-emerald-500 rounded-full" />
                  <div className="h-1 w-12 bg-blue-500 rounded-full" />
                  <div className="h-1 w-8 bg-purple-500 rounded-full" />
                </div>
              </div>

              <div className="space-y-3 font-mono">
                <div className="text-2xl text-white">
                  <span className="text-emerald-400">$</span> Software Engineer
                </div>
                <div className="text-sm text-slate-400 pl-6">
                  <span className="text-blue-400">--spec</span>="HPC & Fintech"
                </div>
                <div className="text-sm text-slate-400 pl-6">
                  <span className="text-blue-400">--edu</span>="MSc Informatics Engineering"
                </div>
              </div>

              <div className="bg-slate-900/50 border border-emerald-500/30 rounded-lg p-5 backdrop-blur-sm font-mono text-sm">
                <div className="text-emerald-400 mb-2"># Core Competencies</div>
                <div className="space-y-1 text-slate-300">
                  <div>├─ High Performance Computing</div>
                  <div>├─ Parallel & Distributed Systems</div>
                  <div>├─ Fintech Backend Architecture</div>
                  <div>├─ Particle Physics (CERN ATLAS)</div>
                  <div>└─ Scalable System Design</div>
                </div>
              </div>

              <div className="flex gap-4">
                <Button size="lg" onClick={() => scrollToSection('contact')} className="bg-emerald-600 hover:bg-emerald-700 text-white font-mono shadow-lg shadow-emerald-500/30">
                  <Terminal className="w-4 h-4 mr-2" />
                  connect()
                </Button>
                <Button size="lg" variant="outline" onClick={() => scrollToSection('experience')} className="border-2 border-emerald-600 text-emerald-400 hover:bg-emerald-950 font-mono">
                  <Code2 className="w-4 h-4 mr-2" />
                  logs
                </Button>
              </div>
            </div>

            {/* HPC Compute Cluster Grid */}
            <div className="relative hidden lg:block">
              <Card className="bg-slate-900/50 border-emerald-500/30 backdrop-blur-sm overflow-hidden" data-testid="hpc-node-grid">
                <CardHeader className="border-b border-emerald-500/20 pb-3">
                  <div className="flex items-center justify-between font-mono text-sm">
                    <div className="flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-emerald-400" />
                      <span className="text-emerald-400">compute_cluster.status</span>
                    </div>
                    <div className="flex items-center gap-2 cursor-pointer" onClick={handleOverclock} title="Click to overclock">
                      <span className={`w-2 h-2 rounded-full ${overclocked ? 'bg-red-400' : 'bg-emerald-400'} animate-pulse`} />
                      <span className={`text-xs ${overclocked ? 'text-red-400' : 'text-emerald-400'}`}>{overclocked ? 'OVERCLOCKED' : 'LIVE'}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 pb-4">
                  {/* Node Grid */}
                  <div className="grid grid-cols-10 gap-1 mb-4">
                    {nodeStates.map((node) => {
                      const colorMap = {
                        idle: 'bg-slate-700/60 border-slate-600/40',
                        computing: 'bg-emerald-500/40 border-emerald-400/60 node-computing',
                        active: 'bg-blue-500/40 border-blue-400/60 node-active',
                        transferring: 'bg-purple-500/30 border-purple-400/50 node-transfer',
                        complete: 'bg-cyan-500/30 border-cyan-400/50',
                      };
                      return (
                        <div
                          key={node.id}
                          className={`aspect-square rounded-sm border transition-all duration-500 ${colorMap[node.state]}`}
                          title={`Node ${String(node.id).padStart(2, '0')} — ${node.state} (${node.load}%)`}
                        />
                      );
                    })}
                  </div>

                  {/* Legend */}
                  <div className="flex flex-wrap gap-3 text-xs font-mono text-slate-400 mb-3 border-t border-emerald-500/10 pt-3">
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500/50 inline-block" /> computing</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500/50 inline-block" /> active</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-purple-500/40 inline-block" /> transfer</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-cyan-500/40 inline-block" /> done</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-700/60 inline-block" /> idle</span>
                  </div>

                  {/* Live Transaction Feed */}
                  <div className="mb-4 border border-blue-500/20 rounded bg-slate-950/60 overflow-hidden" data-testid="txn-feed">
                    <div className="flex items-center justify-between px-3 py-1.5 bg-blue-500/10 border-b border-blue-500/15">
                      <div className="flex items-center gap-1.5 font-mono text-[10px] text-blue-400 tracking-wider">
                        <TrendingUp className="w-3 h-3" />
                        TRANSACTION FEED
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                        <span className="font-mono text-[10px] text-blue-400/70">STREAM</span>
                      </div>
                    </div>
                    <div className="divide-y divide-slate-800/50">
                      {txnFeed.map((txn) => (
                        <div key={txn.id} className="flex items-center justify-between px-3 py-1.5 font-mono text-[11px] txn-row-enter">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-600">{txn.hash}</span>
                            <span className="text-slate-300">{txn.pair}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-emerald-400">${Number(txn.amount).toLocaleString()}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              txn.type === 'SETTLED' ? 'bg-emerald-500/15 text-emerald-400' :
                              txn.type === 'CLEARING' ? 'bg-yellow-500/15 text-yellow-400' :
                              txn.type === 'VERIFIED' ? 'bg-blue-500/15 text-blue-400' :
                              'bg-cyan-500/15 text-cyan-400'
                            }`}>{txn.type}</span>
                            <span className="text-slate-600 w-10 text-right">{txn.ms}ms</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Cluster Stats - Compact */}
                  <div className="flex items-center justify-between font-mono text-xs text-slate-400 px-1">
                    <span><span className="text-emerald-400 font-bold">40</span> nodes</span>
                    <span className="text-slate-700">|</span>
                    <span><span className="text-blue-400 font-bold">2.8</span> PFlops</span>
                    <span className="text-slate-700">|</span>
                    <span><span className="text-purple-400 font-bold">98.7%</span> eff</span>
                    <span className="text-slate-700">|</span>
                    <span><span className="text-cyan-400 font-bold">1.2k</span> txn/s</span>
                  </div>
                </CardContent>
              </Card>

              {/* Profile Photo */}
              <div className="absolute -bottom-12 -right-8">
                <div className="relative p-1 rounded-full bg-gradient-to-br from-emerald-500 via-blue-500 to-purple-500 shadow-2xl shadow-emerald-500/30 cursor-pointer" onClick={() => { setPhotoGlitch(true); setTimeout(() => setPhotoGlitch(false), 600); }}>
                  <div className={`w-40 h-40 rounded-full overflow-hidden bg-slate-900 border-4 border-slate-900 ${photoGlitch ? 'photo-glitch' : ''}`}>
                    <img src="https://customer-assets.emergentagent.com/job_pro-landing-29/artifacts/bc5dv18k_FB_IMG_1554564668270.jpg" alt="John Maia" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-16 px-6 relative overflow-hidden">
        {/* CERN Particle Collision Background */}
        <canvas ref={(el) => { canvasRef.current = el; if (el && !animRef.current) initParticles(); }} className="absolute inset-0 w-full h-full opacity-60 pointer-events-none" />

        <div className="max-w-6xl mx-auto reveal relative z-10">
          <div className="font-mono text-emerald-400 text-sm mb-4">$ cat ~/about.md</div>
          <h2 className="text-4xl font-black font-mono mb-6 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-400">
            About
          </h2>
          <div className="prose prose-invert max-w-none">
            <p className="text-lg text-slate-300 leading-relaxed mb-6">
              Senior Software Engineer specializing in <span className="text-emerald-400 font-semibold">High Performance Computing</span> and <span className="text-blue-400 font-semibold">Fintech</span>. MSc in Informatics Engineering from Universidade do Minho with specialization in parallel computing and additional training in <span className="text-purple-400 font-semibold">Particle Physics</span> (CERN ATLAS).
            </p>
            <p className="text-lg text-slate-300 leading-relaxed">
              Currently building scalable architectures for financial technology, applying HPC expertise to handle complex computational workloads at scale.
            </p>
          </div>
        </div>
      </section>

      {/* Experience */}
      <section id="experience" className="py-16 px-6 relative bg-slate-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="reveal mb-10">
            <div className="font-mono text-emerald-400 text-sm mb-4">$ cat ~/career.log</div>
            <h2 className="text-4xl font-black font-mono text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
              Experience
            </h2>
          </div>

          <div className="space-y-6">
            {experiences.map((exp, i) => (
              <Card key={i} className="reveal bg-slate-900/50 border-slate-800 hover:border-emerald-500/50 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 font-mono">
                      <div className="flex items-center gap-3 mb-2">
                        <Terminal className="w-5 h-5 text-emerald-400 shrink-0" />
                        <h3 className="text-lg font-bold text-white">{exp.role}</h3>
                        {exp.current && <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">ACTIVE</Badge>}
                      </div>
                      <div className="text-purple-400 mb-1">{exp.company}</div>
                      <div className="text-sm text-slate-400 flex flex-wrap gap-x-4 gap-y-1">
                        <span>{exp.period}</span>
                        <span className="text-emerald-400">{exp.duration}</span>
                        <span>{exp.location}</span>
                      </div>
                      {exp.desc && <div className="text-sm text-slate-500 mt-2">{exp.desc}</div>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Education & Publication */}
      <section id="education" className="py-16 px-6 relative">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Education */}
            <div className="reveal">
              <div className="mb-6">
                <div className="font-mono text-emerald-400 text-sm mb-4">$ cat ~/credentials.json</div>
                <h2 className="text-4xl font-black font-mono text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-purple-400">
                  Education
                </h2>
              </div>
              <div className="bg-slate-900/50 border border-emerald-500/30 rounded-lg overflow-hidden font-mono text-sm">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/50 border-b border-emerald-500/20">
                  <span className="w-3 h-3 rounded-full bg-red-500/70" />
                  <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
                  <span className="w-3 h-3 rounded-full bg-emerald-500/70" />
                  <span className="text-slate-400 ml-2 text-xs">credentials.json</span>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <GraduationCap className="w-4 h-4 text-emerald-400" />
                      <span className="text-white">MSc Informatics Engineering</span>
                      <span className="text-slate-600 text-xs ml-auto">2016</span>
                    </div>
                    <div className="text-purple-400 text-xs ml-6">Universidade do Minho</div>
                    <div className="text-slate-500 text-xs ml-6 mt-1">Specialization in Parallel Computing (HPC)</div>
                    <div className="text-slate-500 text-xs ml-6">Additional training in Particle Physics</div>
                  </div>
                  <div className="border-t border-slate-800/50 pt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <GraduationCap className="w-4 h-4 text-blue-400" />
                      <span className="text-white">BSc Informatics Engineering</span>
                      <span className="text-slate-600 text-xs ml-auto">2014</span>
                    </div>
                    <div className="text-purple-400 text-xs ml-6">Universidade do Minho</div>
                    <div className="text-slate-500 text-xs ml-6 mt-1">Computer Engineering</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Publication */}
            <div className="reveal">
              <div className="mb-6">
                <div className="font-mono text-emerald-400 text-sm mb-4">$ cat ~/publications.json</div>
                <h2 className="text-4xl font-black font-mono text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">
                  Publication
                </h2>
              </div>
              <div className="bg-slate-900/50 border border-emerald-500/30 rounded-lg overflow-hidden font-mono text-sm">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/50 border-b border-emerald-500/20">
                  <span className="w-3 h-3 rounded-full bg-red-500/70" />
                  <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
                  <span className="w-3 h-3 rounded-full bg-emerald-500/70" />
                  <span className="text-slate-400 ml-2 text-xs">publications.json</span>
                </div>
                <div className="p-4 space-y-2">
                  <div className="text-slate-500">&#123;</div>
                  <div className="pl-4 space-y-1">
                    <div><span className="text-emerald-400">"title"</span><span className="text-slate-600">:</span> <span className="text-white">"Scheduling Scientific Workloads on an Heterogeneous Server"</span><span className="text-slate-600">,</span></div>
                    <div><span className="text-blue-400">"author"</span><span className="text-slate-600">:</span> <span className="text-slate-300">"John Maia"</span><span className="text-slate-600">,</span></div>
                    <div><span className="text-purple-400">"school"</span><span className="text-slate-600">:</span> <span className="text-slate-300">"Universidade do Minho"</span><span className="text-slate-600">,</span></div>
                    <div><span className="text-emerald-400">"year"</span><span className="text-slate-600">:</span> <span className="text-slate-300">2016</span><span className="text-slate-600">,</span></div>
                    <div><span className="text-blue-400">"type"</span><span className="text-slate-600">:</span> <span className="text-slate-300">"Master's Thesis"</span><span className="text-slate-600">,</span></div>
                    <div><span className="text-purple-400">"supervisors"</span><span className="text-slate-600">:</span> <span className="text-slate-300">["Alberto Proença", "André Pereira"]</span></div>
                  </div>
                  <div className="text-slate-500">&#125;</div>
                  <a href="https://repositorium.uminho.pt/entities/publication/2548c738-db88-4582-9030-7e17ae7b91c1" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 mt-3 pt-3 border-t border-emerald-500/10 text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="publication-link">
                    <FileText className="w-4 h-4" />
                    <span>View full publication</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills */}
      <section id="skills" className="py-16 px-6 relative">
        <div className="max-w-6xl mx-auto">
          {/* Two-column grid: Skills | Languages */}
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Skills Column */}
            <div className="reveal">
              <div className="mb-6">
                <div className="font-mono text-emerald-400 text-sm mb-4">$ htop --sort-key=CPU</div>
                <h2 className="text-4xl font-black font-mono text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                  Skills
                </h2>
              </div>

              <div className="bg-slate-900/50 border border-emerald-500/30 rounded-lg overflow-hidden font-mono text-sm">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/50 border-b border-emerald-500/20">
                  <span className="w-3 h-3 rounded-full bg-red-500/70" />
                  <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
                  <span className="w-3 h-3 rounded-full bg-emerald-500/70" />
                  <span className="text-slate-400 ml-2 text-xs">htop — johnmaia@dev</span>
                </div>

                <div className="grid grid-cols-[40px_1fr_70px_100px] gap-1 px-3 py-2 border-b border-slate-700/50 text-xs text-slate-500 bg-slate-800/30">
                  <span>PID</span>
                  <span>PROCESS</span>
                  <span>STATUS</span>
                  <span>CPU%</span>
                </div>

                <div className="divide-y divide-slate-800/40">
                  {skills.map((skill) => {
                    const barColor = skill.cpu > 90 ? 'bg-emerald-400' : skill.cpu > 80 ? 'bg-blue-400' : 'bg-purple-400';
                    const textColor = skill.cpu > 90 ? 'text-emerald-400' : skill.cpu > 80 ? 'text-blue-400' : 'text-purple-400';
                    return (
                      <div key={skill.pid} className="grid grid-cols-[40px_1fr_70px_100px] gap-1 px-3 py-2 items-center hover:bg-slate-800/30 transition-colors">
                        <span className="text-slate-600 text-xs">{skill.pid}</span>
                        <span className="text-white text-xs">{skill.name}</span>
                        <span className={`text-[10px] ${skill.status === 'RUNNING' ? 'text-emerald-400' : 'text-blue-400'}`}>{skill.status}</span>
                        <div className="flex items-center gap-1">
                          <div className="flex-1 h-2.5 bg-slate-800 rounded-sm overflow-hidden">
                            <div className={`h-full ${barColor} rounded-sm`} style={{ width: `${skill.cpu}%` }} />
                          </div>
                          <span className={`text-[10px] w-7 text-right ${textColor}`}>{skill.cpu}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="px-3 py-2 border-t border-slate-700/50 text-xs text-slate-500 flex gap-4 bg-slate-800/30">
                  <span>Tasks: <span className="text-emerald-400">8</span></span>
                  <span>Running: <span className="text-emerald-400">{skills.filter(s => s.status === 'RUNNING').length}</span></span>
                  <span>Avg: <span className="text-blue-400">{(skills.reduce((a, s) => a + s.cpu, 0) / skills.length).toFixed(1)}%</span></span>
                </div>
              </div>
            </div>

            {/* Languages Column */}
            <div className="reveal">
              <div className="mb-6">
                <div className="font-mono text-emerald-400 text-sm mb-4">$ locale --list</div>
                <h2 className="text-4xl font-black font-mono text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-400">
                  Languages
                </h2>
              </div>

              <div className="bg-slate-900/50 border border-emerald-500/30 rounded-lg overflow-hidden font-mono text-sm">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/50 border-b border-emerald-500/20">
                  <span className="w-3 h-3 rounded-full bg-red-500/70" />
                  <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
                  <span className="w-3 h-3 rounded-full bg-emerald-500/70" />
                  <span className="text-slate-400 ml-2 text-xs">locale --list</span>
                </div>
                <div className="p-4 space-y-2">
                  <div className="text-slate-500">$ locale --list</div>
                  <div className="flex items-center gap-3">
                    <span className="text-emerald-400">LANG</span><span className="text-slate-600">=</span><span className="text-white">pt_PT.UTF-8</span>
                    <span className="text-slate-600 ml-2">#</span><span className="text-slate-400">native</span>
                    <span className="ml-auto text-emerald-400/60">████████████</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-blue-400">LANG</span><span className="text-slate-600">=</span><span className="text-white">en_US.UTF-8</span>
                    <span className="text-slate-600 ml-2">#</span><span className="text-slate-400">fluent</span>
                    <span className="ml-auto text-blue-400/60">██████████</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-purple-400">LANG</span><span className="text-slate-600">=</span><span className="text-white">es_ES.UTF-8</span>
                    <span className="text-slate-600 ml-2">#</span><span className="text-slate-400">intermediate</span>
                    <span className="ml-auto text-purple-400/60">██████</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact — full width below */}
          <div id="contact" className="mt-10 reveal">
            <div className="font-mono text-emerald-400 text-sm mb-4">$ ./connect.sh</div>
            <h2 className="text-4xl font-black font-mono mb-6 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-400">
              Contact
            </h2>
            <div className="max-w-2xl bg-slate-900/50 border border-emerald-500/30 rounded-lg overflow-hidden font-mono text-sm">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/50 border-b border-emerald-500/20">
                <span className="w-3 h-3 rounded-full bg-red-500/70" />
                <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
                <span className="w-3 h-3 rounded-full bg-emerald-500/70" />
                <span className="text-slate-400 ml-2 text-xs">connect.sh</span>
              </div>
              <div className="p-5 space-y-3">
                <div className="text-slate-500">$ ./connect.sh</div>
                <div className="text-emerald-400">Establishing connection...</div>
                <div className="text-slate-400">Parameters loaded. Channels available:</div>

                <div className="space-y-2 py-3">
                  <a href="mailto:hi@johnmaia.dev" className="flex items-center justify-between px-4 py-3 rounded border border-emerald-500/20 bg-slate-800/30 hover:bg-emerald-500/10 hover:border-emerald-500/40 transition-all group" data-testid="contact-email">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-emerald-400" />
                      <span className="text-white">hi@johnmaia.dev</span>
                    </div>
                    <span className="text-emerald-400 text-xs group-hover:translate-x-1 transition-transform">OPEN &gt;</span>
                  </a>
                  <a href="https://www.linkedin.com/in/john-maia/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-4 py-3 rounded border border-blue-500/20 bg-slate-800/30 hover:bg-blue-500/10 hover:border-blue-500/40 transition-all group" data-testid="contact-linkedin">
                    <div className="flex items-center gap-3">
                      <Linkedin className="w-4 h-4 text-blue-400" />
                      <span className="text-white">/in/john-maia</span>
                    </div>
                    <span className="text-blue-400 text-xs group-hover:translate-x-1 transition-transform">OPEN &gt;</span>
                  </a>
                  <a href="https://github.com/johnmaia" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-4 py-3 rounded border border-slate-500/20 bg-slate-800/30 hover:bg-slate-500/10 hover:border-slate-500/40 transition-all group" data-testid="contact-github">
                    <div className="flex items-center gap-3">
                      <Github className="w-4 h-4 text-slate-300" />
                      <span className="text-white">/johnmaia</span>
                    </div>
                    <span className="text-slate-300 text-xs group-hover:translate-x-1 transition-transform">OPEN &gt;</span>
                  </a>
                </div>

                <div className="border-t border-emerald-500/10 pt-3 space-y-1">
                  <div><span className="text-emerald-400">status</span><span className="text-slate-600">:</span> <span className="text-slate-300">Available for <span className="text-emerald-400">opportunities</span> & <span className="text-blue-400">consulting</span></span></div>
                  <div><span className="text-emerald-400">spec</span><span className="text-slate-600">:</span> <span className="text-slate-300">HPC & Fintech</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-6 border-t border-emerald-500/20 bg-slate-950/50 backdrop-blur-xl font-mono text-xs">
        <div className="max-w-6xl mx-auto">
          <div className="bg-slate-900/30 border border-slate-800/50 rounded px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-slate-500">
              <span className="text-slate-600">johnmaia@dev</span><span className="text-emerald-400">:</span><span className="text-blue-400">~</span><span className="text-slate-600">$</span> <span className="text-slate-400">echo $COPYRIGHT</span>
            </div>
            <div className="text-slate-500">© 2026 John Maia</div>
            <div className="flex items-center gap-4">
              <a href="mailto:hi@johnmaia.dev" className="text-slate-500 hover:text-emerald-400 transition-colors">hi@johnmaia.dev</a>
              <span className="text-slate-700">|</span>
              <a href="https://www.linkedin.com/in/john-maia/" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-blue-400 transition-colors">/in/john-maia</a>
              <span className="text-slate-700">|</span>
              <a href="https://github.com/johnmaia" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-200 transition-colors">github</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Easter Egg: Secret Terminal */}
      {showTerminal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) setShowTerminal(false); }}>
          <div className="w-full max-w-lg mx-4 bg-slate-900 border border-emerald-500/40 rounded-lg overflow-hidden shadow-2xl shadow-emerald-500/20 font-mono text-sm" data-testid="secret-terminal">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/80 border-b border-emerald-500/20">
              <span className="w-3 h-3 rounded-full bg-red-500/70 cursor-pointer" onClick={() => setShowTerminal(false)} />
              <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
              <span className="w-3 h-3 rounded-full bg-emerald-500/70" />
              <span className="text-slate-400 ml-2 text-xs">johnmaia@dev ~ secret terminal</span>
            </div>
            <div className="p-4 h-64 overflow-y-auto flex flex-col gap-1">
              {termHistory.map((entry, i) => (
                <div key={i}>
                  {entry.type === 'cmd' && <div><span className="text-emerald-400">$ </span><span className="text-white">{entry.text}</span></div>}
                  {entry.type === 'output' && <div className="text-slate-300 pl-2">{entry.text}</div>}
                  {entry.type === 'system' && <div className="text-slate-500">{entry.text}</div>}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 px-4 py-3 border-t border-slate-800">
              <span className="text-emerald-400">$</span>
              <input
                type="text"
                value={termInput}
                onChange={(e) => setTermInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && termInput.trim()) { handleTermCommand(termInput); setTermInput(''); } }}
                className="flex-1 bg-transparent text-white outline-none font-mono text-sm"
                placeholder="type a command..."
                autoFocus
              />
            </div>
          </div>
        </div>
      )}

      {/* Easter Egg: Konami Code Flying Duck */}
      {duckFly && (
        <div className="fixed inset-0 z-[200] pointer-events-none overflow-hidden">
          <div className="duck-fly absolute text-6xl" style={{ top: '40%' }}>
            🦆
          </div>
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-mono text-emerald-400 text-xl bg-slate-900/90 px-6 py-3 rounded border border-emerald-500/40 konami-msg">
            $ sudo hire johnmaia --force
          </div>
        </div>
      )}

      {/* Easter Egg: Hack Mode */}
      {hackMode && (
        <div className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center pointer-events-none overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(40)].map((_, i) => (
              <div key={i} className="absolute font-mono text-emerald-500 text-xs hack-rain" style={{ left: `${i * 2.5}%`, animationDelay: `${Math.random() * 2}s`, animationDuration: `${1.5 + Math.random() * 2}s` }}>
                {Array.from({ length: 40 }, () => String.fromCharCode(0x30A0 + Math.random() * 96)).join('')}
              </div>
            ))}
          </div>
          <div className="relative z-10 font-mono text-center">
            <div className="text-emerald-400 text-2xl mb-2 hack-type">ACCESS GRANTED</div>
            <div className="text-emerald-600 text-sm">Decrypting mainframe... root@johnmaia-hpc</div>
            <div className="text-emerald-700 text-xs mt-4">just kidding. hire me instead → hi@johnmaia.dev</div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Landing;
